// @ts-check
import { gql } from '@apollo/client/core';
import React from 'react';
import BookmarkIcon from '../../components/BookmarkIcon';
import {
  Box,
  Flex,
  Page,
  QRCode,
  Registration,
  Text,
  Title,
} from '../../pdf-design-system';
import {
  PagesProvider,
  usePage,
} from '../../pdf-design-system/Base/PageLayout/Page';
import UserLocaleProvider from '../../Providers/UserLocaleProvider';
import messages from './locale/messages';
export default function TwoExhibitorsPerPage({ data, locale, ...rest }) {
  const { collection } = data;
  const sections = afterQuery(data, rest);
  return (
    <PagesProvider>
      <UserLocaleProvider locale={locale} messages={messages}>
        {Array.from(Array(Math.ceil(sections.length / 2))).map((_, index) => (
          <PageLayout key={index}>
            <CatalogPage
              tables={sections.slice(index * 2, index * 2 + 2)}
              theme={theme}
              baseUrl={collection?.site?.publicUrl}
              {...rest}
            />
          </PageLayout>
        ))}
      </UserLocaleProvider>
    </PagesProvider>
  );
}

/** @param {{ children: React.ReactNode; bleed?: number; style?: import('@react-pdf/types').Style; [x: string]: any }} props */
function PageLayout({ children, bleed = 0, style, ...rest }) {
  if (bleed)
    return (
      <Page
        size={{ width: `${162 + 2 * bleed}mm`, height: `${234 + 2 * bleed}mm` }}
        style={[
          { paddingVertical: `${bleed}mm`, paddingHorizontal: `${bleed}mm` },
          style,
        ]}
        theme={theme}
        {...rest}
      >
        <Box
          style={{
            paddingHorizontal: '9mm',
            paddingVertical: '8mm',
            height: '234mm',
            position: 'relative',
          }}
        >
          {children}
        </Box>
      </Page>
    );
  return (
    <Page
      size={{ width: '4.25in', height: '9in' }}
      style={[
        {
          paddingVertical: '0.2in',
          paddingHorizontal: 0,
        },
        style,
      ]}
      theme={theme}
      {...rest}
    >
      {children}
    </Page>
  );
}

const standardPadding = '0.4in';

function CatalogPage({ tables, theme, baseUrl, ...rest }) {
  const { alignment } = usePage();
  return (
    <Box>
      {tables.map((table) => {
        const { registration } = table;
        const isSeekingDistribution =
          registration.registeredProducts.nodes.find(
            (product) => product.producer.isSeekingDistribution
          );
        return (
          <Box key={registration.registrationId} style={{ height: '4.2in' }}>
            <Registration.Provider value={getTable(table)}>
              <Flex
                direction={alignment === 'left' ? 'row' : 'row-reverse'}
                style={{
                  width: '100%',
                  marginBottom: 4,
                  ...(alignment === 'left'
                    ? {
                        paddingLeft: standardPadding,
                      }
                    : { paddingRight: standardPadding }),
                }}
              >
                <QRCode
                  url={`${baseUrl}/e/${registration.companyId}`}
                  errorCorrectionLevel="L"
                  margin={0}
                  style={{
                    width: '20mm',
                    height: '20mm',
                    marginBottom: 5,
                    flexShrink: 0,
                    ...(alignment === 'left'
                      ? { marginRight: 3 }
                      : { marginLeft: 3 }),
                  }}
                />

                <Flex
                  direction="column"
                  style={{
                    width: '100%',
                    marginBottom: 5,
                  }}
                >
                  <Title
                    style={{
                      marginBottom: 2,
                      padding: 4,
                      color: 'white',
                      backgroundColor: 'black',
                      ...(alignment === 'left'
                        ? { paddingRight: standardPadding }
                        : { paddingLeft: standardPadding }),
                    }}
                  >
                    {registration.name}
                  </Title>
                  <Flex
                    direction="column"
                    style={{
                      flexGrow: 1,

                      ...(alignment === 'left'
                        ? { paddingRight: 0, alignItems: 'flex-start' }
                        : {
                            paddingLeft: standardPadding,
                            textAlign: 'right',
                            alignItems: 'flex-end',
                          }),
                    }}
                  >
                    <Text variant="small" style={{ color: '#666' }}>
                      {registration.stand?.name || ' '}
                    </Text>
                    {isSeekingDistribution && (
                      <Badge style={{ marginTop: 2 }}>
                        Seeking distribution
                      </Badge>
                    )}
                    <Box style={{ flexGrow: 1 }}></Box>
                    <Text variant="small" style={{ color: '#666' }}>
                      Scan to take notes
                    </Text>
                  </Flex>
                </Flex>
              </Flex>
              {registration.registeredProducts?.nodes?.map((product) => (
                <Box
                  key={product.productId}
                  style={{
                    height: '.8in',
                    paddingVertical: 2,
                    paddingHorizontal: standardPadding,
                  }}
                >
                  {product.bookmark && (
                    <Flex
                      direction="column"
                      justify="center"
                      style={{
                        position: 'absolute',
                        left: 8,
                        top: 2,
                        alignItems: 'center',
                      }}
                    >
                      <Box
                        style={{
                          border: 1,
                          borderColor: '#ccc',
                          borderRadius: 100,
                          marginBottom: 6,
                          backgroundColor: '#efefef',
                        }}
                      >
                        <BookmarkIcon size="xsmall" />
                      </Box>
                      {product.tastingNote?.rating && (
                        <Text>{product.tastingNote.rating}/5</Text>
                      )}
                    </Flex>
                  )}
                  <Text style={{ marginBottom: 2 }}>
                    {product.product.name}
                  </Text>
                  <Flex style={{ marginBottom: 2 }}>
                    <Text variant="small" style={{ color: '#666' }}>
                      {getAvaFromRegionHierarchy(product.product)}
                    </Text>
                    {product?.newToMarket?.value && (
                      <Badge style={{ marginLeft: 2 }}>New!</Badge>
                    )}
                  </Flex>
                  <Text
                    variant="small"
                    style={{ color: '#666', marginBottom: 2 }}
                  >
                    {getVarietyString(product.product)}
                  </Text>
                  <Text variant="small">
                    {product.tastingNote?.note
                      ? `My note: ${product.tastingNote.note}`
                      : ' '}
                  </Text>
                </Box>
              ))}
            </Registration.Provider>
          </Box>
        );
      })}
      <Text variant="small" style={{ textAlign: 'center', color: '#666' }}>
        More information available on calwine.ca
      </Text>
    </Box>
  );
}

function Badge({ children, style }) {
  return (
    <Text
      variant="smallest"
      style={[
        {
          color: 'white',
          paddingHorizontal: 3,
          paddingVertical: 1,
          paddingTop: 1.5,
          backgroundColor: 'black',
          borderRadius: 100,
          lineHeight: 1,
        },
        style,
      ]}
    >
      {children}
    </Text>
  );
}

function getVarietyString({ grapeVarieties }) {
  // Don't show 100% for wines with only 1 variety
  if (grapeVarieties?.length === 1) return grapeVarieties[0].varietyName;
  return grapeVarieties
    .map(({ varietyName, percentage }) => {
      return percentage ? `${percentage}% ${varietyName}` : `${varietyName}`;
    })
    .join(', ');
}

function getAvaFromRegionHierarchy({ regionHierarchy }) {
  if (regionHierarchy.length <= 3)
    return regionHierarchy[regionHierarchy.length - 1];
  return `${regionHierarchy[3]}, ${regionHierarchy[2]}`;
}

/**
 * @param {ReturnType<afterQuery>[number]} section
 */
function getTable(section) {
  if (section.__typename === 'RegistrationCatalogSection') {
    return section.registration;
  }
  return undefined;
}

/** @typedef {ReturnType<getTable>} Table */

/**
 * @typedef {import('../../pdf-design-system/useTheme').BottlebooksPDFTheme["theme"] & {
 *  colors?: Record<string, string>;
 * }} Theme
 */

/** @type {Theme} */
export const theme = {
  components: {
    Text: {
      // base: { ...dinLight },
      default: { fontSize: 10, lineHeight: 7.8 / 6.5 },
      smallest: { fontSize: 6, lineHeight: 1 },
      small: { fontSize: 8, lineHeight: 1.2 },
      medium: { /* ...dinMedium , */ fontSize: 6.5, lineHeight: 7.8 / 6.5 },
    },
    Title: {
      base: { /*  ...dinMedium , */ textTransform: 'uppercase' },
      small: {
        fontSize: 8,
        lineHeight: 1,
      },
      default: {
        fontSize: 10,
        lineHeight: 1,
      },
      large: {
        fontSize: 18,
        lineHeight: 1.3,
      },
    },
  },
};

TwoExhibitorsPerPage.fragment = gql`
  fragment TwoExhibitorsPerPage on CatalogConfiguration {
    catalogConfigurationId
    collectionId
    collection {
      collectionId
      eventId: collectionId
      site {
        publicUrl
      }
    }
    sections(filter: { registration: { companyId: { in: $companyIds } } }) {
      __typename
      ... on RegistrationCatalogSection {
        registration {
          registrationId
          companyId
        }
      }
      ... on UploadedFileCatalogSection {
        filename
      }
      ...TwoExhibitorsPerPage_Table
    }
  }

  fragment TwoExhibitorsPerPage_Table on RegistrationCatalogSection {
    registration {
      registrationId
      name
      companyId
      stand {
        name
      }

      registeredProducts {
        nodes {
          newToMarket: customFieldValue(
            key: "isThisWineNewNewDiscoveryOrNewToMarket"
          ) {
            __typename
            ... on YesNoFieldValue {
              value
            }
          }
          producer {
            isSeekingDistribution
          }
          productId
          product {
            productId
            name
            regionHierarchy
            denomination
            ... on Wine {
              grapeVarieties {
                varietyName
                percentage
              }
            }
          }
        }
      }
    }
  }
`;

function afterQuery(data, rest) {
  if (rest.companyId) {
    return data.sections?.filter(
      (section) =>
        section.__typename === 'RegistrationCatalogSection' &&
        section.registration.companyId === rest.companyId
    );
  }
  if (rest.registrationId) {
    return data.sections?.filter(
      (section) =>
        section.__typename === 'RegistrationCatalogSection' &&
        section.registration.registrationId === rest.registrationId
    );
  }

  return data.sections;
}
